import { useCallback, useMemo, useState, type ReactNode } from 'react';
import '@/styles/pages.css';
import {
  CLAIM_STATUSES,
  fmtDate,
  money,
  OWNED_STAGES,
  periodLabel,
  statusLabel,
  type ClaimStatus,
  type User,
} from '@gdec/shared';
import { api } from '@/api/client';
import { StatusBadge } from '@/components/Badge';
import { ClaimViewer } from '@/components/ClaimViewer';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { ActionDialog, ErrorNote, QueuePanel, useAction, useQueue } from '@/components/QueueKit';
import { LoadingBlock } from '@/components/Spinner';
import { useToast } from '@/components/Toast';
import { useDirectory } from '@/components/admin/useDirectory';

interface AdminClaimRow {
  id: string;
  period: string;
  status: ClaimStatus;
  claimedAmount: number;
  financeReviewedAmount: number | null;
  finalApprovedAmount: number | null;
  releasedAmount: number | null;
  voidReason: string;
  createdAt: number;
  updatedAt: number;
  requesterName: string;
  requesterDepartment: string;
  financeReviewerName: string | null;
  financeApproverName: string | null;
  treasuryUserName: string | null;
  rowCount: number;
}

type Pending =
  | { kind: 'void'; row: AdminClaimRow }
  | { kind: 'reassign'; row: AdminClaimRow }
  | { kind: 'release'; row: AdminClaimRow };

/**
 * Every claim in the system, with the administrative actions.
 *
 * Note what is deliberately absent: there is no way to edit an amount or undo a
 * decision. An administrator decides *who* acts and whether a claim proceeds at
 * all — never what the outcome was. Editing an approved figure after the fact is
 * exactly what the amount ladder and the audit trail exist to prevent.
 */
export function AdminRequestsPage(): ReactNode {
  const { toast } = useToast();
  const { users } = useDirectory();

  const [status, setStatus] = useState<ClaimStatus | ''>('');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState<{ status: string; search: string }>({
    status: '',
    search: '',
  });

  const load = useCallback(
    () =>
      api.admin.listClaims<AdminClaimRow[]>({
        ...(applied.status ? { status: applied.status } : {}),
        ...(applied.search ? { search: applied.search } : {}),
      }),
    [applied],
  );
  const { rows, error, reload } = useQueue<AdminClaimRow>(load);

  const [viewing, setViewing] = useState<AdminClaimRow | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const action = useAction();

  /** Only a claim someone has taken can be handed over or released. */
  const isOwned = (r: AdminClaimRow): boolean => Boolean(OWNED_STAGES[r.status]);

  const eligibleOwners = useMemo(
    () => users.filter((u: User) => u.active).sort((x, y) => x.name.localeCompare(y.name)),
    [users],
  );

  const run = useCallback(async (): Promise<void> => {
    if (!pending) return;
    const { kind, row } = pending;
    const ok = await action.run(async () => {
      if (kind === 'void') await api.admin.voidClaim<unknown>({ claimId: row.id, reason });
      else if (kind === 'reassign')
        await api.admin.reassignClaim<unknown>({ claimId: row.id, userId: newOwner, reason });
      else await api.admin.releaseClaimOwnership<unknown>({ claimId: row.id });
    });
    if (!ok) return;
    toast(
      kind === 'void'
        ? `${row.id} voided.`
        : kind === 'reassign'
          ? `${row.id} reassigned.`
          : `${row.id} returned to its queue.`,
      'ok',
    );
    setPending(null);
    setReason('');
    setNewOwner('');
    await reload();
  }, [pending, reason, newOwner, action, toast, reload]);

  const columns: ReadonlyArray<Column<AdminClaimRow>> = useMemo(
    () => [
      { key: 'id', header: 'Request ID', cell: (r) => <b>{r.id}</b> },
      { key: 'who', header: 'Requestor', cell: (r) => `${r.requesterName} · ${r.requesterDepartment}` },
      { key: 'period', header: 'Period', cell: (r) => periodLabel(r.period) },
      { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
      { key: 'claimed', header: 'Claimed', align: 'right', cell: (r) => money(r.claimedAmount) },
      {
        key: 'released',
        header: 'Released',
        align: 'right',
        cell: (r) => (r.releasedAmount == null ? '—' : money(r.releasedAmount)),
      },
      {
        key: 'owner',
        header: 'Held by',
        cell: (r) =>
          r.financeReviewerName ?? r.financeApproverName ?? r.treasuryUserName ?? (
            <span className="muted">—</span>
          ),
      },
      { key: 'created', header: 'Submitted', cell: (r) => fmtDate(r.createdAt) },
      {
        key: 'actions',
        header: '',
        cell: (r) => (
          <div className="row-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setViewing(r)}>
              View
            </button>
            {isOwned(r) ? (
              <>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setPending({ kind: 'reassign', row: r })}
                >
                  Reassign
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setPending({ kind: 'release', row: r })}
                >
                  Release
                </button>
              </>
            ) : null}
            {r.status !== 'Voided' && r.status !== 'Released' ? (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => setPending({ kind: 'void', row: r })}
              >
                Void
              </button>
            ) : null}
          </div>
        ),
      },
    ],
    [],
  );

  if (rows === null && error === null) return <LoadingBlock label="Loading every claim…" />;

  const voidBlocked = pending?.kind === 'void' && reason.trim() === '';
  const reassignBlocked = pending?.kind === 'reassign' && newOwner === '';

  return (
    <>
      <ErrorNote message={error} />

      <QueuePanel title="All Requests" count={rows?.length ?? 0}>
        <p className="hint">
          Every claim in the system. Administrators can reassign a stuck claim or void one
          outright — but never change an amount or a decision, which is what the audit trail is
          for.
        </p>

        <div className="filter-row">
          <div className="field">
            <label htmlFor="ar-search">Search</label>
            <input
              id="ar-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Request ID or requestor"
            />
          </div>
          <div className="field">
            <label htmlFor="ar-status">Status</label>
            <select
              id="ar-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as ClaimStatus | '')}
            >
              <option value="">All statuses</option>
              {CLAIM_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {statusLabel(s)}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-actions">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setApplied({ status, search: search.trim() })}
            >
              Apply
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setStatus('');
                setSearch('');
                setApplied({ status: '', search: '' });
              }}
            >
              Clear
            </button>
          </div>
        </div>

        <DataTable
          rows={rows ?? []}
          columns={columns}
          rowKey={(r) => r.id}
          empty={<EmptyState icon="📄" title="No claim matches those filters." />}
        />
      </QueuePanel>

      {viewing ? (
        <ClaimViewer
          claimId={viewing.id}
          requesterName={viewing.requesterName}
          onClose={() => setViewing(null)}
        />
      ) : null}

      {pending ? (
        <ActionDialog
          title={
            pending.kind === 'void'
              ? `Void ${pending.row.id}`
              : pending.kind === 'reassign'
                ? `Reassign ${pending.row.id}`
                : `Return ${pending.row.id} to its queue`
          }
          confirmLabel={
            pending.kind === 'void'
              ? 'Void this claim'
              : pending.kind === 'reassign'
                ? 'Reassign'
                : 'Release'
          }
          danger={pending.kind === 'void'}
          onConfirm={() => void run()}
          onCancel={() => {
            setPending(null);
            setReason('');
            setNewOwner('');
          }}
          busy={action.busy}
          disabled={voidBlocked || reassignBlocked}
          error={action.error}
        >
          {pending.kind === 'void' ? (
            <>
              <p className="dialog-lead">
                This ends {pending.row.id} permanently. Nothing is deleted — the claim, its
                receipts and its history stay on the record, marked as voided by you with the
                reason below. {pending.row.requesterName} will be notified.
              </p>
              <div className="field">
                <label htmlFor="void-reason">
                  Reason <span className="req">(required)</span>
                </label>
                <textarea
                  id="void-reason"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Duplicate of EXP-2026-00012, submitted in error."
                />
              </div>
            </>
          ) : pending.kind === 'reassign' ? (
            <>
              <p className="dialog-lead">
                Hand this claim to someone else at its current stage
                {' '}(<b>{statusLabel(pending.row.status)}</b>). Amounts and decisions are not
                changed — only who acts next.
              </p>
              <div className="field">
                <label htmlFor="new-owner">Assign to</label>
                <select
                  id="new-owner"
                  value={newOwner}
                  onChange={(e) => setNewOwner(e.target.value)}
                >
                  <option value="">Choose an employee…</option>
                  {eligibleOwners.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} — {u.department}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="reassign-reason">Reason (optional)</label>
                <input
                  id="reassign-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Original reviewer on leave"
                />
              </div>
            </>
          ) : (
            <p className="dialog-lead">
              This returns {pending.row.id} to the shared queue so anyone with the right role can
              take it. The current holder loses the claim; nothing else changes.
            </p>
          )}
        </ActionDialog>
      ) : null}
    </>
  );
}
